/** Gzip sidecar for sql.js bytes in IndexedDB (raw SQLite magic kept for legacy rows). */

function canStreamGzip(): boolean {
  return (
    typeof CompressionStream !== 'undefined' &&
    typeof DecompressionStream !== 'undefined' &&
    typeof Blob !== 'undefined' &&
    typeof Blob.prototype.stream === 'function'
  );
}

export function looksLikeGzip(bytes: Uint8Array): boolean {
  return bytes.length >= 2 && bytes[0] === 0x1f && bytes[1] === 0x8b;
}

export async function decodeSqliteIdbBytes(stored: Uint8Array): Promise<Uint8Array> {
  if (!looksLikeGzip(stored)) return stored;
  if (!canStreamGzip()) {
    throw new Error('IndexedDB SQLite blob is gzip-compressed but stream gzip decode is unavailable');
  }
  const out = await new Response(
    new Blob([stored as BlobPart]).stream().pipeThrough(new DecompressionStream('gzip')),
  ).arrayBuffer();
  return new Uint8Array(out);
}

export function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.byteLength !== b.byteLength) return false;
  for (let i = 0; i < a.byteLength; i++) if (a[i] !== b[i]) return false;
  return true;
}

/** Store gzip only when smaller than raw export. */
export async function encodeSqliteIdbBytes(raw: Uint8Array): Promise<Uint8Array> {
  if (!canStreamGzip()) return raw;
  const out = await new Response(
    new Blob([raw as BlobPart]).stream().pipeThrough(new CompressionStream('gzip')),
  ).arrayBuffer();
  const compressed = new Uint8Array(out);
  return compressed.byteLength < raw.byteLength ? compressed : raw;
}

export type SqliteIdbStoredCache = { bytes?: Uint8Array };

/** Returns true when IndexedDB blob was updated. */
export async function putSqliteExportIfChanged(
  putStored: (stored: Uint8Array) => Promise<void>,
  raw: Uint8Array,
  cache: SqliteIdbStoredCache,
): Promise<boolean> {
  const stored = await encodeSqliteIdbBytes(raw);
  if (cache.bytes && bytesEqual(stored, cache.bytes)) return false;
  await putStored(stored);
  cache.bytes = stored;
  return true;
}
